import { ReactNode, useEffect, useState } from 'react';
import {
  MdOutlineAddCircle,
  MdOutlineLocalOffer,
  MdTableChart,
} from 'react-icons/md';
import { useDocumentTitle, useMediaQuery } from 'usehooks-ts';
import { FleetCard, EmptyFleetCard, FleetList } from '@components/FleetList';
import { PricePerMileModal } from '@components/PricePerMileModal';
import InfiniteScroll from 'react-infinite-scroller';
import { InfiniteScrollLoading } from '@components/InfiniteScroll';
import { useAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Button } from '../../tailwindComponents/Button';
import { MasterImportModal } from '../../tailwindComponents/MasterImportModal';
import { showAiFeaturesAtom } from '../../store/flags';
import { PendingImportsBanner } from '../MasterDataImportReview/PendingImportsBanner';
import { isUploadFleetModalOpenAtom } from '../../store/jotai';
import {
  FLEET_CATEGORIES,
  FLEET_GET,
  LSP_WAREHOUSES_URL,
  MASTER_DATA_IMPORT_CREATE,
  UPLOAD_FLEET_MASTER_URL,
} from '../../constants';
import { useAuthHttp } from '../../hooks';
import {
  VehicleSummary as Vehicle,
  fleetMapping,
  fleetSubtitleMapping,
} from './types';

export function FleetMaster() {
  const [isUploadFleetModalOpen, setIsUploadFleetModalOpen] = useAtom(
    isUploadFleetModalOpenAtom
  );
  const [isFleetLoading, setIsFleetLoading] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [emptyTilesByType, setEmptyTilesByType] = useState<
    Record<string, number>
  >({});
  const [declaredCounts, setDeclaredCounts] = useState<
    Record<string, number>
  >({});
  const [pricePerMileVehicleType, setPricePerMileVehicleType] = useState<
    string | null
  >(null);
  const [isPricePerMileOpen, setIsPricePerMileOpen] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const showAiFeatures = useAtomValue(showAiFeaturesAtom);

  const authHttp = useAuthHttp();
  const navigate = useNavigate();

  const isTablet = useMediaQuery('(min-width: 48em)');
  const isDesktop = useMediaQuery('(min-width: 62em)');

  useDocumentTitle('FLOX - Fleet');

  const fetchFleetAndWarehouses = async () => {
    try {
      const [fleetResponse, warehouseResponse] = await Promise.all([
        authHttp.post(FLEET_GET, {
          pagination: { itemsPerPage: isDesktop ? 100 : 25 },
        }),
        authHttp.post(LSP_WAREHOUSES_URL, {}),
      ]);

      const fetchedVehicles: Vehicle[] = fleetResponse.data?.vehicles ?? [];
      setVehicles(fetchedVehicles);

      // Aggregate declared fleet counts from all warehouses
      const warehouses = warehouseResponse.data?.warehouses ?? [];
      const declaredFleetCounts: Record<string, number> = {};
      warehouses.forEach(
        (w: { fleet?: { type: string; count: number }[] }) => {
          w.fleet?.forEach((f) => {
            declaredFleetCounts[f.type] =
              (declaredFleetCounts[f.type] || 0) + f.count;
          });
        }
      );

      // Count actual vehicles per type
      const actualCountsByType: Record<string, number> = {};
      fetchedVehicles.forEach((v) => {
        actualCountsByType[v.vehicleType] =
          (actualCountsByType[v.vehicleType] || 0) + 1;
      });

      // Compute gaps
      const gaps: Record<string, number> = {};
      Object.entries(declaredFleetCounts).forEach(([type, declared]) => {
        const actual = actualCountsByType[type] || 0;
        if (declared > actual) {
          gaps[type] = declared - actual;
        }
      });
      setEmptyTilesByType(gaps);
      setDeclaredCounts(declaredFleetCounts);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsFleetLoading(false);
    }
  };

  const handleViewVehicleSummary = (vehicleId: string) => {
    navigate(`/vehicle/${vehicleId}`);
  };

  useEffect(() => {
    fetchFleetAndWarehouses();
  }, []);

  const handleImported = () => {
    fetchFleetAndWarehouses();
    setRefreshCounter((count) => count + 1);
  };

  const sortedVehicles = vehicles.sort((a, b) =>
    a.vehicleType.toUpperCase().localeCompare(b.vehicleType.toUpperCase())
  );

  const groupedVehicles: { [key: string]: Vehicle[] } = {};

  sortedVehicles.forEach((vehicle) => {
    const { vehicleType } = vehicle;
    if (!groupedVehicles[vehicleType]) {
      groupedVehicles[vehicleType] = [];
    }
    groupedVehicles[vehicleType].push(vehicle);
  });

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Fleet', href: '/fleet', current: true },
  ];

  return (
    <>
      <PageHeader header='Fleet' pages={pages}>
        <div className='flex items-center gap-2'>
          <Button
            label='Import Vehicles'
            intent='secondary'
            startIcon={<MdTableChart />}
            onClick={() => setIsUploadFleetModalOpen(true)}
          />
          <Button
            intent='primary'
            label='Add Vehicle'
            onClick={() => navigate('/vehicle/add')}
            startIcon={<MdOutlineAddCircle />}
          />
        </div>
      </PageHeader>
      <MasterImportModal
        isOpen={isUploadFleetModalOpen}
        setIsOpen={setIsUploadFleetModalOpen}
        title='Import vehicles'
        templateHref='/files/fleet_master_template.xlsx'
        templateLabel='Download excel template'
        templateCaption='One row per vehicle — fields marked * are required.'
        uploadUrl={UPLOAD_FLEET_MASTER_URL}
        uploadFieldName='productFile'
        dropzoneId='fleet-master-dropzone'
        onUploaded={handleImported}
        aiImportUrl={showAiFeatures ? MASTER_DATA_IMPORT_CREATE : undefined}
        entityType='Vehicle'
        onAiImportCreated={(importId) =>
          navigate(`/master-data/imports/${importId}`)
        }
      />
      <PricePerMileModal
        vehicleType={pricePerMileVehicleType}
        isOpen={isPricePerMileOpen}
        onClose={() => setIsPricePerMileOpen(false)}
      />
      <PendingImportsBanner entityType='Vehicle' refreshKey={refreshCounter} />
      <main className='flex flex-col gap-4 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] p-4 lg:pt-4 lg:px-8 lg:pb-8 relative'>
        <InfiniteScroll
          element={FleetList as unknown as ReactNode}
          initialLoad
          loadMore={() => {}}
          hasMore={false}
          useWindow={false}
          getScrollParent={() => document.getElementById('product-list')}
          loader={
            <InfiniteScrollLoading
              key='fleet-loader'
              loadingMessage='Loading vehicles...'
              sx={{ gridColumn: '1 / -1' }}
            />
          }
        >
          {isFleetLoading && !vehicles.length && (
            <InfiniteScrollLoading
              key='fleet-loader'
              loadingMessage='Loading vehicles...'
              sx={{ gridColumn: '1 / -1' }}
            />
          )}
          {!isFleetLoading && !vehicles.length && Object.keys(emptyTilesByType).length === 0 && (
            <div className='col-span-full flex flex-col items-center justify-center py-24'>
              <div className='flex flex-col items-center text-center max-w-md'>
                {/* Empty state icon */}
                <div className='mb-6'>
                  <svg
                    className='w-16 h-16 text-gray-300'
                    viewBox='0 0 64 64'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M32 4L4 16V48L32 60L60 48V16L32 4Z'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinejoin='round'
                      fill='none'
                    />
                    <path
                      d='M32 28L4 16'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                    />
                    <path
                      d='M32 28L60 16'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                    />
                    <path
                      d='M32 28V60'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                    />
                    <circle cx='32' cy='18' r='3' fill='currentColor' />
                  </svg>
                </div>
                <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                  Your Fleet is empty
                </h2>
                <p className='text-gray-500 text-sm mb-6'>
                  Get started by importing new vehicles through bulk upload via
                  an Excel file or by manually entering details using our Fleet
                  master.
                </p>
                <div className='flex flex-row items-center gap-3'>
                  <Button
                    label='Import (Excel)'
                    intent='primary'
                    startIcon={<MdTableChart />}
                    onClick={() => setIsUploadFleetModalOpen(true)}
                  />
                  <Button
                    label='Add Manually'
                    intent='secondary'
                    startIcon={<MdOutlineAddCircle />}
                    onClick={() => navigate('/vehicle/add')}
                  />
                </div>
              </div>
            </div>
          )}
          <div className='flex flex-col gap-9'>
            {(() => {
              // Merge vehicle groups and empty-only groups into a single sorted list
              const allTypes = new Set([
                ...Object.keys(groupedVehicles),
                ...Object.keys(emptyTilesByType),
              ]);
              const sortedTypes = Array.from(allTypes).sort((a, b) =>
                (fleetMapping[a] || a)
                  .toUpperCase()
                  .localeCompare((fleetMapping[b] || b).toUpperCase())
              );

              return sortedTypes.map((vehicleType) => {
                const typeVehicles = groupedVehicles[vehicleType] ?? [];
                const emptyCount = emptyTilesByType[vehicleType] ?? 0;
                if (typeVehicles.length === 0 && emptyCount === 0) return null;

                return (
                  <div key={vehicleType} className='flex flex-col gap-3'>
                    <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                      <div className='flex items-baseline gap-2'>
                        <h2 className='text-lg font-semibold text-gray-900'>
                          {fleetMapping[vehicleType] || vehicleType}
                        </h2>
                        <span className='text-sm text-gray-500'>
                          {fleetSubtitleMapping[vehicleType] || ''}
                        </span>
                        {declaredCounts[vehicleType] > 0 && (
                          <span className='text-sm text-gray-400'>
                            Total # of vehicles: {declaredCounts[vehicleType]}
                          </span>
                        )}
                      </div>
                      {FLEET_CATEGORIES.some(
                        (category) => category.value === vehicleType
                      ) && (
                        <button
                          type='button'
                          aria-label={`Price per mile for ${
                            fleetMapping[vehicleType] || vehicleType
                          }`}
                          onClick={() => {
                            setPricePerMileVehicleType(vehicleType);
                            setIsPricePerMileOpen(true);
                          }}
                          className='flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
                        >
                          <MdOutlineLocalOffer className='h-4 w-4' />
                          Price per mile
                        </button>
                      )}
                    </div>
                    <div className='flex flex-row flex-wrap gap-4'>
                      {typeVehicles.map((vehicle, index) => (
                        <FleetCard
                          {...(index === 0 && { id: 'first' })}
                          key={vehicle.vehicleId}
                          vehicleSummary={vehicle}
                          orientation={isTablet ? 'horizontal' : 'vertical'}
                          onClick={() =>
                            handleViewVehicleSummary(vehicle.vehicleId)
                          }
                        />
                      ))}
                      {Array.from({ length: emptyCount }).map((_, i) => (
                        <EmptyFleetCard
                          key={`empty-${vehicleType}-${i}`}
                          vehicleType={vehicleType}
                          displayName={
                            fleetMapping[vehicleType] || vehicleType
                          }
                          onClick={() =>
                            navigate(`/vehicle/add?type=${vehicleType}`)
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </InfiniteScroll>
      </main>
    </>
  );
}
